import { useMemo, useState } from "react";
import { siteData } from "../copy";

/**
 * The interactive priorities demo (the prototype's priViz). Abi scores a plan as
 * Σ 2^importance × min(completion, 1): overfunding a priority earns nothing. The demo allocates the
 * household's monthly surplus by 2^score, caps each priority at what it needs, redistributes the
 * excess, and repeats. Do not substitute a linear weighting (docs/BACKEND-HANDOFF.md). The amounts
 * in site-copy.json are tuned: two priorities can finish fully funded, all three never can, and
 * every single-point move changes all three percentages.
 */
export type Priority = { n: string; v: number; need: number };

export function allocate(rows: Priority[], surplus: number): number[] {
  const got = rows.map(() => 0);
  let pool = surplus;
  let open = rows.map((_, i) => i);
  for (let pass = 0; pass < 12 && pool > 0.5 && open.length; pass++) {
    const tot = open.reduce((a, i) => a + Math.pow(2, rows[i].v), 0);
    if (!tot) break;
    let spent = 0;
    const filled: number[] = [];
    open.forEach((i) => {
      const want = (pool * Math.pow(2, rows[i].v)) / tot;
      const room = Math.max(0, rows[i].need - got[i]);
      const give = Math.min(want, room);
      got[i] += give;
      spent += give;
      if (got[i] >= rows[i].need - 0.5) filled.push(i);
    });
    pool -= spent;
    if (spent < 0.5) break;
    open = open.filter((i) => !filled.includes(i));
  }
  return got;
}

const money = (n: number) => `$${Math.round(n).toLocaleString("en-CA")}`;

export function PrioritiesDemo() {
  const initial = useMemo(
    () =>
      siteData<[string, number, number][]>("home.pri.rows").map(([n, v, need]) => ({ n, v, need })),
    [],
  );
  // The surplus is the copy key alone (siteData throws when it is missing): no typed-in fallback.
  const surplus = siteData<number>("home.pri.surplusAmt");
  const [rows, setRows] = useState<Priority[]>(initial);
  const got = allocate(rows, surplus);

  return (
    <div id="priviz">
      {rows.map((r, i) => {
        const pct = Math.max(0, Math.min(1, got[i] / r.need));
        const full = pct >= 0.999;
        return (
          <div className="prow" key={r.n}>
            <div className="lab">
              <b>{r.n}</b>
              <span className="sc">{r.v}/10</span>
            </div>
            <input
              type="range"
              min={1}
              max={10}
              value={r.v}
              aria-label={`${r.n} importance, 1 to 10`}
              onChange={(e) => {
                const v = Number(e.target.value);
                setRows((prev) => prev.map((x, j) => (j === i ? { ...x, v } : x)));
              }}
            />
            <div className="shr">
              <span className="track">
                <span
                  className={`fill${full ? " full" : ""}`}
                  style={{ width: `${(pct * 100).toFixed(1)}%` }}
                />
              </span>
              <span className={`pct${full ? " full" : ""}`} data-testid={`pri-pct-${i}`}>
                {Math.round(pct * 100)}%
              </span>
            </div>
            <div className="pfund">
              {full ? (
                <>
                  <span className="ok">Fully funded</span> at {money(r.need)} a month
                </>
              ) : (
                <>
                  {money(got[i])} of the {money(r.need)} a month it needs
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
